import { settingTypes } from '@ipfabric/utils';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, type FormikProps, useFormik } from 'formik';
import { type FC, memo, useCallback, useMemo } from 'react';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { hasHttpOrHttps } from 'utils/hasHttpOrHtttps';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlField } from './fields/FormikVendorApiBaseUrlField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

export type ArubaNewCentralFormValues =
  BaseVendorFormValues<settingTypes.ArubaNewCentralApiSettings>;

type Props = VendorDialogProps<
  ArubaNewCentralFormValues,
  settingTypes.ArubaNewCentralApiSettings,
  settingTypes.UpdateArubaNewCentralApiSettings
>;

const resetSecretAttributes = (
  values: ArubaNewCentralFormValues,
): ArubaNewCentralFormValues => {
  return {
    ...values,
    clientId: '',
    clientSecret: '',
  };
};

/** Whether the selected auth mode is one of the GreenLake-backed flows. */
const isGreenlakeLoginType = (values: ArubaNewCentralFormValues): boolean =>
  values.loginType === settingTypes.arubaAuthTypes.glp ||
  values.loginType === settingTypes.arubaAuthTypes.msp;

/**
 * Validate the auth-mode-specific fields: GLP/MSP need a GreenLake workspace id,
 * Central has no extra fields.
 */
const validateAuthMode = (
  values: ArubaNewCentralFormValues,
  requiredMsg: string,
): FormikErrors<ArubaNewCentralFormValues> => {
  const errors: FormikErrors<ArubaNewCentralFormValues> = {};

  if (isGreenlakeLoginType(values) && !values.tenantId) {
    errors.tenantId = requiredMsg;
  }
  return errors;
};

const BareArubaNewCentralForm: FC<Props> = (props) => {
  const editMode = props.dialogType === 'edit';
  const msg = useLocale();
  const { validations, arubaNewCentralForm } = msg.discoverySettings.vendorsApi;

  const loginTypeOptions = useMemo(
    () => [
      {
        label: arubaNewCentralForm.loginTypeOptions.central,
        value: settingTypes.arubaAuthTypes.central,
      },
      {
        label: arubaNewCentralForm.loginTypeOptions.glp,
        value: settingTypes.arubaAuthTypes.glp,
      },
      {
        label: arubaNewCentralForm.loginTypeOptions.msp,
        value: settingTypes.arubaAuthTypes.msp,
      },
    ],
    [arubaNewCentralForm],
  );

  const validateHttpUrl = useCallback(
    (value?: string): string | undefined => {
      if (!value) {
        return validations.required;
      }
      if (!hasHttpOrHttps(value)) {
        return validations.baseUrlStartsWith;
      }
      return undefined;
    },
    [validations],
  );

  const formik = useFormik<ArubaNewCentralFormValues>({
    initialValues: resetSecretAttributes(props.initialValues),
    validate: (values) => {
      const errors: FormikErrors<ArubaNewCentralFormValues> = {};

      const baseUrlError = validateHttpUrl(values.baseUrl);
      if (baseUrlError) {
        errors.baseUrl = baseUrlError;
      }
      Object.assign(errors, validateAuthMode(values, validations.required));

      // Secrets are only required when creating (on edit, blank means "unchanged").
      const secretFields = ['clientId', 'clientSecret'] as const;
      for (const field of secretFields) {
        if (!editMode && !values[field]) {
          errors[field] = validations.required;
        }
      }

      validateSlug(values, errors, msg);

      return errors;
    },
    onSubmit: makeFormikOnSubmit((values: ArubaNewCentralFormValues) => {
      // Prune auth-discriminated fields down to the ones valid for the selected
      // mode: the backend Joi schema forbids `tenantId` for Central.
      const { tenantId, loginType, ...rest } = {
        ...values,
        ...getAdvancedSettingsDefaultValues(values),
      };
      const authFields = isGreenlakeLoginType(values)
        ? { loginType, tenantId }
        : { loginType: settingTypes.arubaAuthTypes.central };

      const payload = { ...rest, ...authFields };

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...payload,
          id: values.id,
          clientId:
            values.clientId || props.initialValues.clientId || undefined,
          clientSecret:
            values.clientSecret ||
            props.initialValues.clientSecret ||
            undefined,
        });
      } else if (props.dialogType === 'add') {
        props.onSubmit(payload);
      }
    }),
  });

  return (
    <VendorDialogContainer
      formik={formik as FormikProps<ArubaNewCentralFormValues>}
      dialogType={props.dialogType}
      {...props.vendorDialogContainerPassthruProps}
    >
      <FormikVendorApiEnabledField formik={formik} />
      <FormikVendorApiBaseUrlField
        formik={formik}
        placeholder='https://de1.api.central.arubanetworks.com'
      />
      <FormikSelectField
        formik={formik}
        label={arubaNewCentralForm.loginType}
        name='loginType'
        nonClearable
        options={loginTypeOptions}
      />
      {isGreenlakeLoginType(formik.values) && (
        <FormikTextField
          formik={formik}
          label={arubaNewCentralForm.tenantId}
          name='tenantId'
          type='text'
        />
      )}
      <FormikTextField
        formik={formik}
        label={arubaNewCentralForm.clientId}
        placeholder={editMode ? '(unchanged)' : undefined}
        name='clientId'
        type='text'
      />
      <FormikTextField
        formik={formik}
        label={arubaNewCentralForm.clientSecret}
        placeholder={editMode ? '(unchanged)' : undefined}
        name='clientSecret'
        type='password'
      />
      <FormikVendorApiSlugField formik={formik} />
      <FormikVendorApiCommentField formik={formik} />
      <FormikVendorApiRejectUnauthorizedField formik={formik} />
      <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
      <FormikAdvancedSettings>
        <FormikRateLimitingSettingsFields formik={formik} />
      </FormikAdvancedSettings>
    </VendorDialogContainer>
  );
};

export const ArubaNewCentralForm = memo(BareArubaNewCentralForm);
